import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { stripePromise } from "../../lib/stripe";
import { supabase } from "../../supabaseClient";

export default function VisitorSpaceBookingForm({ space, slot, slots, onClose }) {
  const [bookingType, setBookingType] = useState("hourly");
  const [quantity, setQuantity] = useState(1);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("10:00");
  const [durationHours, setDurationHours] = useState(2);
  const [durationMinutes, setDurationMinutes] = useState(0);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingBookings, setExistingBookings] = useState([]);
  const [bookedSeats, setBookedSeats] = useState(0);

  const selectedSlots = Array.isArray(slots) && slots.length
    ? [...slots].sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())
    : slot
    ? [slot]
    : [];
  const primarySlot = selectedSlots[0] || null;
  const selectedTotalCents = selectedSlots.reduce((sum, s) => sum + (Number(s?.price_cents) || 0), 0);
  const selectedCurrency = primarySlot?.currency || null;
  const selectedStartAt = primarySlot?.start_at || null;
  const selectedEndAt = selectedSlots.length
    ? selectedSlots[selectedSlots.length - 1]?.end_at
    : null;

  // If slots are provided, pre-fill the form
  useEffect(() => {
    if (selectedSlots.length) {
      const slotStart = new Date(selectedSlots[0].start_at);
      const slotEnd = new Date(selectedSlots[selectedSlots.length - 1].end_at);
      
      // Set date
      const dateStr = slotStart.toISOString().split('T')[0];
      setDate(dateStr);
      
      // Set start time
      const hours = String(slotStart.getHours()).padStart(2, '0');
      const minutes = String(slotStart.getMinutes()).padStart(2, '0');
      setStartTime(`${hours}:${minutes}`);
      
      // Calculate duration
      const diffMs = slotEnd - slotStart;
      const totalMinutes = diffMs / (1000 * 60);
      const durationHrs = Math.floor(totalMinutes / 60);
      const durationMins = totalMinutes % 60;
      setDurationHours(durationHrs);
      setDurationMinutes(durationMins);
    }
  }, [selectedSlots.length, selectedStartAt, selectedEndAt]);

  // Fetch existing bookings for the selected space/date/time
  useEffect(() => {
    if (!space?.id || !date || !startTime) {
      setExistingBookings([]);
      setBookedSeats(0);
      return;
    }

    const fetchBookings = async () => {
      try {
        // Construct the start datetime
        const startDateTime = `${date}T${startTime}:00`;
        
        console.log('[VisitorSpaceBookingForm] Fetching bookings for:', {
          spaceId: space.id,
          date,
          startTime,
          startDateTime
        });

        // Query bookings with space_slots join to filter by space_id
        const { data, error } = await supabase
          .from('bookings')
          .select(`
            id, 
            seats, 
            status,
            space_slot_id,
            space_slots!inner(
              space_id,
              start_at,
              end_at
            )
          `)
          .eq('space_slots.space_id', space.id)
          .gte('space_slots.start_at', `${date}T00:00:00`)
          .lte('space_slots.start_at', `${date}T23:59:59`)
          .in('status', ['pending', 'confirmed', 'paid', 'completed']);

        if (error) {
          console.error('[VisitorSpaceBookingForm] Error fetching bookings:', error);
          return;
        }

        console.log('[VisitorSpaceBookingForm] Found bookings:', data);

        // Calculate total booked seats
        const totalBooked = (data || []).reduce((sum, booking) => {
          return sum + (Number(booking.seats) || 1);
        }, 0);

        setExistingBookings(data || []);
        setBookedSeats(totalBooked);
      } catch (err) {
        console.error('[VisitorSpaceBookingForm] Error:', err);
      }
    };

    fetchBookings();

    // Set up real-time subscription for bookings changes
    const subscription = supabase
      .channel(`bookings-space-${space.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings'
        },
        (payload) => {
          console.log('[VisitorSpaceBookingForm] Real-time booking change:', payload);
          // Refetch bookings when changes occur to filter by our space
          fetchBookings();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [space?.id, date, startTime]);

  if (!space) {
    return null;
  }

  const hourlyRate = space.pricing?.hourly ?? space.pricing?.perHour ?? 0;
  const dailyRate = space.pricing?.daily ?? space.pricing?.perDay ?? 0;

  // Calculate end time based on start time + duration
  const calculateEndTime = () => {
    if (!startTime) return "";
    
    const [startHour, startMin] = startTime.split(":").map(Number);
    const totalMinutes = startHour * 60 + startMin + durationHours * 60 + durationMinutes;
    const endHour = Math.floor(totalMinutes / 60) % 24;
    const endMin = totalMinutes % 60;
    
    return `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
  };

  const endTime = calculateEndTime();

  // Calculate total price
  const calculateTotal = () => {
    if (bookingType === "hourly") {
      const totalHours = durationHours + durationMinutes / 60;
      return (hourlyRate * totalHours * quantity).toFixed(2);
    }
    if (bookingType === "daily") {
      return (dailyRate * quantity).toFixed(2);
    }
    return "0.00";
  };

  const total = calculateTotal();

  // Get space capacity
  const spaceCapacity = space.seats_total ?? space.seatsTotal ?? space.capacity?.seats ?? 4;
  const availableSpots = Math.max(0, spaceCapacity - bookedSeats - quantity);
  const canBook = (bookedSeats + quantity) <= spaceCapacity;

  // Suggested durations (in hours and minutes)
  const suggestedDurations = [
    { label: "1 hour", hours: 1, minutes: 0 },
    { label: "1.5 hours", hours: 1, minutes: 30 },
    { label: "2 hours", hours: 2, minutes: 0 },
    { label: "3 hours", hours: 3, minutes: 0 },
    { label: "4 hours", hours: 4, minutes: 0 },
    { label: "6 hours", hours: 6, minutes: 0 },
    { label: "8 hours", hours: 8, minutes: 0 },
    { label: "Full day", hours: 10, minutes: 0 },
  ];

  const handleSuggestedDuration = (hours, minutes) => {
    setDurationHours(hours);
    setDurationMinutes(minutes);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !email) {
      alert("Please fill in all required fields");
      return;
    }

    // Require slot selection (visitors cannot create slots on-demand)
    if (!selectedSlots.length) {
      alert("Please select an available time slot from the slot picker");
      return;
    }

    // Slot exists, proceed with checkout
    performCheckout(selectedSlots, space);
  };

  const performCheckout = async (slotData, spaceData) => {
    setLoading(true);

    try {
      const slotArray = Array.isArray(slotData) ? slotData : (slotData ? [slotData] : []);
      const slotIds = slotArray.map((s) => s?.id).filter(Boolean);

      console.log("[VisitorSpaceBookingForm] Submitting booking (WORKSHOP PATTERN):", {
        slotIds,
        spaceId: spaceData.id,
        studioId: spaceData.studio_id,
        email,
      });

      // MINIMAL PAYLOAD: Let server derive pricing from slot (workshop pattern)
      const checkoutPayload = {
        studioId: spaceData.studio_id,
        spaceSlotIds: slotIds,
        spaceSlotId: slotIds[0] || null,
        quantity,
        seats: quantity,
        email,
        attendee_name: null, // TODO: Add form fields if needed
        attendee_phone: null,
        attendee_message: null,
        // DO NOT send currency or amount - server derives from slot
      };

      console.log("[VisitorSpaceBookingForm] Checkout payload:", checkoutPayload);

      const response = await fetch("/api/bookings/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(checkoutPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create checkout session");
      }

      const { url } = await response.json();
      
      // Redirect to Stripe Checkout URL
      if (url) {
        window.location.href = url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("Booking error:", err);
      alert(err.message || "Error processing booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-hidden">
      <div className="mudo-glass-panel w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header with Studio Info */}
        <div className="flex-shrink-0 p-6 border-b border-white/10 bg-black/50 flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-semibold text-white/60 uppercase tracking-widest mb-1">
              STUDIO BOOKING
            </p>
            <p className="text-xs font-semibold text-white/60 uppercase tracking-widest mb-3">
              {space.studio?.name || "Studio"}
            </p>
            <h2 className="text-2xl font-bold text-white mb-1">{space.name}</h2>
            <p className="text-xs text-white/60">Manage spaces, schedules, and guest bookings.</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Tab-like section - Book only for visitors */}
        <div className="flex-shrink-0 px-6 pt-6 flex gap-3 border-b border-white/10">
          <button className="px-6 py-2 rounded-full bg-orange-500 text-white font-semibold text-sm hover:bg-orange-600 transition-colors">
            Book
          </button>
        </div>

        {/* Scrollable Form Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Slot Selection Info */}
          {!primarySlot?.id && (
            <div className="px-4 py-3 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-100 text-sm flex items-start gap-2">
              <span className="text-lg mt-0.5">ℹ️</span>
              <div>
                <p className="font-semibold">Manual Booking Mode</p>
                <p className="text-xs text-blue-100/80 mt-1">You can enter your preferred date and time manually, or select a pre-defined time slot for instant availability confirmation.</p>
              </div>
            </div>
          )}

          {/* Slot Information Display */}
          {primarySlot?.id && (
            <div className="px-4 py-3 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-100 text-sm">
              <p className="font-semibold mb-2">✓ Selected Slot</p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <span className="text-emerald-200/70">Date & Time:</span>
                  <p className="text-emerald-50 font-medium">
                    {selectedSlots.length > 1
                      ? `${new Date(selectedStartAt).toLocaleString()} → ${new Date(selectedEndAt).toLocaleString()}`
                      : new Date(primarySlot.start_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <span className="text-emerald-200/70">Price:</span>
                  <p className="text-emerald-50 font-medium">${(selectedTotalCents / 100).toFixed(2)}</p>
                </div>
                <div>
                  <span className="text-emerald-200/70">Slots:</span>
                  <p className="text-emerald-50 font-medium">{selectedSlots.length}</p>
                </div>
              </div>
            </div>
          )}

          {/* Top Info Row */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-white/60">Price</p>
              <p className="text-2xl font-bold text-orange-400">
                ${primarySlot?.price_cents ? (selectedTotalCents / 100).toFixed(2) : total}
              </p>
            </div>
            <div>
              <p className="text-white/60 text-xs">Currency</p>
              <p className="text-sm text-white">
                {selectedCurrency ? selectedCurrency : (space.studio?.settings?.currency || "USD")}
              </p>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Booking Type */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                  Booking Type
                </label>
                <select
                  value={bookingType}
                  onChange={(e) => setBookingType(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 [&>option]:bg-gray-800 [&>option]:text-white"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="hourly" className="bg-gray-800 text-white">Hourly (${hourlyRate})</option>
                  <option value="daily" className="bg-gray-800 text-white">Daily (${dailyRate})</option>
                </select>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                  Quantity
                </label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-center focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Space */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                  Space
                </label>
                <div className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm">
                  {space.name} (alt)
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                />
              </div>
            </div>
          </div>

          {/* Time and Duration Inputs */}
          <div className="space-y-4">
            {/* Start Time */}
            <div>
              <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                Start Time
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
              />
            </div>

            {/* Duration */}
            <div>
              <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                Duration
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/50 mb-1">Hours</label>
                  <input
                    type="number"
                    value={durationHours}
                    onChange={(e) => setDurationHours(Math.max(0, Math.min(23, parseInt(e.target.value) || 0)))}
                    min="0"
                    max="23"
                    className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-center focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">Minutes</label>
                  <input
                    type="number"
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                    min="0"
                    max="59"
                    step="15"
                    className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-center focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                  />
                </div>
              </div>
            </div>

            {/* End Time (Calculated - Read Only Display) */}
            <div>
              <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                End Time (Calculated)
              </label>
              <div className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-center">
                {endTime || "--:--"}
              </div>
            </div>
          </div>

          {/* Choose a Spot Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider">
                Choose a Spot
              </label>
              {date && startTime && endTime && (
                <span className="text-xs text-white/60">{date} • {startTime}–{endTime}</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Render spots based on capacity */}
              {Array.from({ length: spaceCapacity }).map((_, index) => {
                const isAlreadyBooked = index < bookedSeats;
                const isYourBooking = !isAlreadyBooked && index < (bookedSeats + quantity);
                const isAvailable = !isAlreadyBooked && !isYourBooking;
                
                return (
                  <div
                    key={index}
                    className={`w-4 h-4 rounded transition-all ${
                      isAlreadyBooked 
                        ? 'bg-blue-500' 
                        : isYourBooking 
                        ? 'bg-orange-500' 
                        : 'bg-gray-600/50'
                    }`}
                    title={
                      isAlreadyBooked 
                        ? 'Already booked' 
                        : isYourBooking 
                        ? 'Your booking' 
                        : 'Available'
                    }
                  />
                );
              })}
              <span className="text-xs text-white/60 ml-2">
                {availableSpots} of {spaceCapacity} spots available
              </span>
            </div>
            {bookedSeats > 0 && (
              <div className="mt-2 flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-blue-500" />
                  <span className="text-white/60">Already booked ({bookedSeats})</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-orange-500" />
                  <span className="text-white/60">Your booking ({quantity})</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-gray-600/50" />
                  <span className="text-white/60">Available ({availableSpots})</span>
                </div>
              </div>
            )}
            {!canBook && (
              <div className="mt-2 px-3 py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-xs">
                ⚠️ Not enough spots available. Only {spaceCapacity - bookedSeats} spot(s) remaining.
              </div>
            )}
          </div>

          {/* Suggested Durations */}
          <div>
            <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">
              Quick Duration Selection
            </label>
            <div className="grid grid-cols-4 gap-2">
              {suggestedDurations.map((duration) => (
                <button
                  key={duration.label}
                  type="button"
                  onClick={() => handleSuggestedDuration(duration.hours, duration.minutes)}
                  className="px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-xs font-medium hover:bg-white/20 hover:border-white/40 transition-all"
                >
                  {duration.label}
                </button>
              ))}
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
              Email (required for confirmation)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
            />
          </div>

          {/* Action Button */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading || !canBook}
              className="flex-1 px-6 py-3 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Processing..." : !canBook ? "Not enough spots available" : `Book now${slot?.price_cents ? ` - $${(slot.price_cents / 100).toFixed(2)}` : ` - $${total}`}`}
            </button>
          </div>

          <p className="text-xs text-white/50 text-center">
            You'll be redirected to secure payment. Confirmation emails will be sent to you and the studio.
          </p>
          </form>
        </div>
      </div>
    </div>
  );
}
